/**
 * 🌞 Summer50 — Free Access duration helpers (Paul 2026-07-16: "called something
 * that means free and then tag it with the amount of time free").
 * Everything derives from the grant's real dates so 2-week / 1-month / 2-month
 * campaigns all render from the same component with zero new UI.
 *
 * ── A free period has two shapes, and each is counted in its own unit (2026-10-07) ──
 *
 * The server ends a free period in one of two ways (services/free_month_grant_service.py):
 *
 *  - A period that JOINS a running free week ends at the last second of a local day,
 *    23:59:59 (`joined_period_last_moment`). It is a number of CALENDAR days: a
 *    month of 31 is 31 dates, whatever time of day the week began.
 *  - Any other period — redeemed with no week running, deferred to a trial's end,
 *    or stacked on another — runs a whole number of 24-HOUR days from an instant.
 *
 * Until 2026-10-07 every count here, and the two the screens wrote out for
 * themselves, was hours ÷ 24. That is exact for the second shape and wrong for
 * the first, twice over:
 *
 *  - with a clock change before the period's end, the days left were a day out
 *    for an hour a day: "8 days left" at 00:30 and "7" from 01:30, in New York in
 *    the week before 1 November (the clocks going forward: a day short from 23:00);
 *  - the meter measured from the moment the week began. A month of 31 days that
 *    began at 9 PM read "Day 1 of 30" beside "31 days left", and stayed on
 *    "Day 1" until 9 PM the next day.
 *
 * So a period that ends with its day counts days on the phone's calendar, today
 * included — its last day reads "1 day left" all day, as it always did — and any
 * other period is counted exactly as before. No words changed (Matt, 2026-10-07).
 *
 * The phone tells the shapes apart by the period's last moment on its OWN clock.
 * A phone in another zone than the one the server cut the day in reads an end
 * that is not 23:59:59, and counts that period in hours, as it did before.
 *
 * Pure, no React: utils/__tests__/freeAccessDuration.test.ts runs it inside
 * zones that change their clocks.
 */
import { calendarDaysBetween } from './calendarDays';

const DAY_MS = 86_400_000;

/** Does the period end with a day on this phone's calendar: at its last second, as a period that joined a free week does? */
function endsWithItsDay(end: Date): boolean {
  return end.getHours() === 23 && end.getMinutes() === 59 && end.getSeconds() === 59;
}

/** How many days the period is: its dates, first and last included, when it ends with its day; else its 24-hour days. */
function spanDays(start: Date, end: Date): number {
  return endsWithItsDay(end)
    ? calendarDaysBetween(start, end) + 1
    : Math.round((end.getTime() - start.getTime()) / DAY_MS);
}

/** "1 WEEK" | "2 WEEKS" | "1 MONTH" | "2 MONTHS" … from the grant span. */
export function freeAccessPillLabel(startsAt?: string | null, expiresAt?: string | null): string {
  if (!startsAt || !expiresAt) return 'FREE';
  const days = spanDays(new Date(startsAt), new Date(expiresAt));
  // `!(days > 0)` and not `days <= 0`: a date that will not parse makes this NaN, and it read "NaN MONTHS".
  if (!(days > 0)) return 'FREE';
  if (days <= 21) {
    const w = Math.max(1, Math.round(days / 7));
    return w === 1 ? '1 WEEK' : `${w} WEEKS`;
  }
  const m = Math.max(1, Math.round(days / 30.5));
  return m === 1 ? '1 MONTH' : `${m} MONTHS`;
}

/**
 * The days of free access left, for every place that says it: Account's line, the
 * referral drawer's confirm and the paywall's meter. Today counts: the last day
 * reads 1. 0 once the period has ended; null when there is no end to count to —
 * a missing date, or one that will not parse — so that a line is left without a
 * number rather than printed with NaN in it.
 */
export function freeAccessDaysLeft(expiresAt?: string | null, now: Date = new Date()): number | null {
  if (!expiresAt) return null;
  const end = new Date(expiresAt);
  if (Number.isNaN(end.getTime())) return null;
  if (end.getTime() <= now.getTime()) return 0;
  return endsWithItsDay(end)
    ? calendarDaysBetween(now, end) + 1
    : Math.ceil((end.getTime() - now.getTime()) / DAY_MS);
}

/** Day-of-span / total / remaining for the drawer meter. */
export function freeAccessProgress(startsAt?: string | null, expiresAt?: string | null, now: Date = new Date()) {
  if (!startsAt || !expiresAt) return null;
  const start = new Date(startsAt);
  const end = new Date(expiresAt);
  if (!(end.getTime() > start.getTime())) return null;
  const totalDays = Math.max(1, spanDays(start, end));
  // The day it is on: the date's place in the period when it ends with its day (the
  // first date is day 1, from midnight to midnight); else the 24-hour day it is in.
  const dayOf = Math.min(totalDays, Math.max(1, endsWithItsDay(end)
    ? calendarDaysBetween(start, now) + 1
    : Math.ceil((now.getTime() - start.getTime()) / DAY_MS)));
  const daysLeft = freeAccessDaysLeft(expiresAt, now) ?? 0;
  return { dayOf, totalDays, daysLeft, fraction: Math.min(1, Math.max(0, dayOf / totalDays)) };
}
